import type React from 'react';
import { useState } from 'react';
import { enUS, ptBR } from 'date-fns/locale';
import type { DateRange } from 'react-day-picker';
import { useI18nContext } from '@/i18n/i18n-react';
import { Button, buttonVariants } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { mobileLayer } from '@/lib/mobile-layer';
import { cn } from '@/lib/utils';

export interface PeriodSheetProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  initialFrom?: Date;
  initialTo?: Date;
  onApply(from: Date, to: Date): void;
  returnFocusRef?: React.RefObject<HTMLElement | null>;
}

/**
 * Mobile custom period (SHELL-07 AC 3): one month at a time, start and end
 * (a single day counts), nothing after today. "Aplicar" needs both ends.
 */
export function PeriodSheet({
  open,
  onOpenChange,
  initialFrom,
  initialTo,
  onApply,
  returnFocusRef,
}: PeriodSheetProps) {
  const { LL } = useI18nContext();

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        returnFocusRef={returnFocusRef}
        aria-describedby={undefined}
      >
        <DrawerHeader className="pb-0 text-left">
          <DrawerTitle>{LL.shell.period.sheetTitle()}</DrawerTitle>
        </DrawerHeader>
        {/* Mounted only while open, so each opening starts from the props. */}
        <PeriodSheetBody
          initialFrom={initialFrom}
          initialTo={initialTo}
          onApply={(from, to) => {
            onApply(from, to);
            onOpenChange(false);
          }}
        />
      </DrawerContent>
    </Drawer>
  );
}

function PeriodSheetBody({
  initialFrom,
  initialTo,
  onApply,
}: Pick<PeriodSheetProps, 'initialFrom' | 'initialTo' | 'onApply'>) {
  const { LL, locale } = useI18nContext();
  const [today] = useState(() => new Date());
  const [range, setRange] = useState<DateRange | undefined>(
    initialFrom && initialTo ? { from: initialFrom, to: initialTo } : undefined
  );

  // First tap is the start, second the end; a tap after a full range starts over.
  const pick = (day: Date) => {
    if (!range?.from || range.to) {
      setRange({ from: day, to: undefined });
      return;
    }
    setRange(
      day < range.from
        ? { from: day, to: range.from }
        : { from: range.from, to: day }
    );
  };

  const from = range?.from;
  const to = range?.to;

  return (
    <>
      <Calendar
        mode="range"
        numberOfMonths={1}
        selected={range}
        onSelect={(_next, day) => pick(day)}
        defaultMonth={initialFrom ?? today}
        disabled={{ after: today }}
        locale={{ en: enUS, pt: ptBR }[locale]}
        className="mx-auto"
        classNames={{
          head_cell:
            'text-muted-foreground rounded-md w-11 font-normal text-[0.8rem]',
          cell: 'h-11 w-11 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
          day: cn(
            buttonVariants({ variant: 'ghost' }),
            'h-11 w-11 p-0 font-normal aria-selected:opacity-100'
          ),
          nav_button: cn(
            buttonVariants({ variant: 'outline' }),
            'h-11 w-11 bg-transparent p-0 opacity-50 hover:opacity-100'
          ),
        }}
      />
      <DrawerFooter>
        <Button
          type="button"
          className={cn('w-full', mobileLayer.touch)}
          disabled={!from || !to}
          onClick={() => {
            if (from && to) onApply(from, to);
          }}
        >
          {LL.shell.period.apply()}
        </Button>
      </DrawerFooter>
    </>
  );
}
